/** Expired Invoices — invoiced orders nobody paid within 7 days. Off the Invoice Board, kept for reference, one tap to reactivate. */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Hourglass, RotateCcw, Search } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import useHomePath from '@/hooks/useHomePath';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatApiError } from '@/utils/apiError';

const BRAND = {
  prodecor: { label: 'ProDecor', colour: '#D5AB09', ink: '#1f2937' },
  proepoxy: { label: 'ProEpoxy', colour: '#7A0B7A', ink: '#fff' },
  probound: { label: 'ProBound', colour: '#2F5E96', ink: '#fff' },
  material_order: { label: 'Material Order', colour: '#1f4030', ink: '#fff' },
};
const fmtGBP = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const detailPath = (o) => (o.product === 'material_order' ? `/material-orders/${o.id}` : `/${o.product}/orders/${o.id}`);

export default function ExpiredInvoices() {
  const navigate = useNavigate();
  const homePath = useHomePath();
  const { isStaff } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  useEffect(() => { if (!isStaff) navigate('/'); }, [isStaff, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/orders/expired-invoices`, { credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed to load');
      const d = await res.json();
      setRows(d.rows || []); setDays(d.expiry_days || 7);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reactivate = async (o) => {
    if (!window.confirm(`Put ${o.reference} back on the Invoice Board? The installer will be able to pay again and it gets a fresh ${days}-day window.`)) return;
    setBusyId(o.id);
    try {
      const res = await fetch(`${API_URL}/api/staff/orders/${o.product}/${o.id}/reactivate-invoice`, { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed');
      toast.success(`${o.reference} is back on the Invoice Board`);
      setRows((r) => r.filter((x) => x.id !== o.id));
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((o) => [o.reference, o.customer_reference, o.installer_name, o.installer_email, o.invoice_number, o.xero_invoice_number].some((v) => String(v || '').toLowerCase().includes(q)));
  }, [rows, search]);

  if (!isStaff) return null;
  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="text-white" style={{ background: 'linear-gradient(135deg,#0B0B0E 0%,#4a3b12 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/staff/orders')} className="p-2 hover:bg-white/10 rounded-lg shrink-0" data-testid="expired-back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate"><Hourglass className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#D4A24B' }} /> Expired Invoices</h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">Invoiced orders not paid within {days} days leave the Invoice Board and sit here — nothing is deleted. Reactivate one if the customer comes back.</p>
          </div>
          <div className="relative shrink-0 hidden sm:block"><Search className="w-4 h-4 absolute left-3 top-2.5 text-white/60" /><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search reference, customer, invoice…" className="pl-9 h-9 w-64 bg-white/10 border-white/20 text-white placeholder:text-white/50" data-testid="expired-search" /></div>
          <Button onClick={() => navigate(homePath)} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0"><Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Home</span></Button>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-6 space-y-3">
        <div className="text-xs text-gray-500" data-testid="expired-count">{loading ? 'Loading…' : `${filtered.length} expired invoice${filtered.length === 1 ? '' : 's'}`}</div>
        {!loading && filtered.length === 0 && (
          <Card className="p-12 text-center" data-testid="expired-empty"><Hourglass className="w-12 h-12 mx-auto text-gray-300 mb-3" /><h3 className="font-bold text-gray-900 mb-1">{search.trim() ? 'No matches' : 'No expired invoices'}</h3><p className="text-gray-500 text-sm">Invoices unpaid for {days} days will appear here automatically.</p></Card>
        )}
        {filtered.map((o) => {
          const brand = BRAND[o.product] || {};
          return (
            <Card key={`${o.product}-${o.id}`} className="p-4" data-testid={`expired-row-${o.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <button type="button" onClick={() => navigate(detailPath(o))} className="min-w-0 flex-1 text-left" data-testid={`expired-open-${o.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: brand.colour, color: brand.ink }}>{brand.label || o.product}</span>
                    <span className="font-mono text-xs text-gray-700">{o.reference}</span>
                    {(o.xero_invoice_number || o.invoice_number) && <span className="text-[10px] font-mono text-indigo-700">Inv: {o.xero_invoice_number || o.invoice_number}</span>}
                  </div>
                  <div className="font-semibold text-sm text-gray-900 mt-1 truncate">{o.customer_reference || o.installer_name || 'No reference'}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">{o.installer_name}{o.installer_email ? ` · ${o.installer_email}` : ''}</div>
                  <div className="text-[11px] text-amber-800 mt-1">Invoiced {o.invoiced_at ? new Date(o.invoiced_at).toLocaleDateString('en-GB') : '—'} · expired {o.invoice_expired_at ? new Date(o.invoice_expired_at).toLocaleDateString('en-GB') : '—'}</div>
                </button>
                <div className="text-right shrink-0">
                  <div className="text-lg font-bold text-gray-900">{fmtGBP(o.confirmed_total ?? o.total)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">{o.confirmed_total != null ? 'confirmed' : 'estimate'}</div>
                  <Button size="sm" variant="outline" className="mt-2 border-emerald-300 text-emerald-800 hover:bg-emerald-50" disabled={busyId === o.id} onClick={() => reactivate(o)} data-testid={`expired-reactivate-${o.id}`}><RotateCcw className="w-3.5 h-3.5 mr-1" /> {busyId === o.id ? 'Working…' : 'Reactivate'}</Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
